import React, { useState } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar,
  CalendarDays,
  LayoutGrid,
  History
} from 'lucide-react';
import type { ViewGranularity } from '../../types/dashboard.types';

interface DateSelectorProps {
  selectedMonth: string; // YYYY-MM format
  selectedDate?: string; // YYYY-MM-DD format
  viewGranularity: ViewGranularity;
  onMonthChange: (month: string) => void;
  onDateChange: (date: string) => void;
  onGranularityChange: (granularity: ViewGranularity) => void;
  canViewDaily: boolean;
  className?: string;
}

const DateSelector: React.FC<DateSelectorProps> = ({
  selectedMonth,
  selectedDate,
  viewGranularity,
  onMonthChange,
  onDateChange,
  onGranularityChange,
  canViewDaily,
  className = '',
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  const currentDate = new Date();
  const currentMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
  const currentDateStr = currentDate.toISOString().split('T')[0];
  
  // Parse selected values
  const [year, month] = selectedMonth.split('-').map(Number);
  
  // Format displays
  const formatMonthDisplay = (dateStr: string) => {
    const [y, m] = dateStr.split('-').map(Number);
    const date = new Date(y, m - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };
  
  const formatDateDisplay = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  // Navigation handlers
  const goToPreviousMonth = () => {
    const prevDate = new Date(year, month - 2, 1);
    const newMonth = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
    onMonthChange(newMonth);
  };
  
  const goToNextMonth = () => {
    const nextDate = new Date(year, month, 1);
    const nextMonth = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}`;
    if (nextMonth <= currentMonth) {
      onMonthChange(nextMonth);
    }
  };
  
  const goToPreviousDay = () => {
    if (!selectedDate) return;
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    const newDate = date.toISOString().split('T')[0];
    onDateChange(newDate);
  };
  
  const goToNextDay = () => {
    if (!selectedDate) return;
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    const newDate = date.toISOString().split('T')[0];
    if (newDate <= currentDateStr) {
      onDateChange(newDate);
    }
  };
  
  const goToToday = () => {
    onDateChange(currentDateStr);
  };
  
  const goToCurrentMonth = () => {
    onMonthChange(currentMonth);
  };
  
  const isCurrentMonth = selectedMonth === currentMonth;
  const isToday = selectedDate === currentDateStr;
  const canGoNextMonth = selectedMonth < currentMonth;
  const canGoNextDay = selectedDate ? selectedDate < currentDateStr : false;

  // Generate days in month for date picker
  const getDaysInMonth = () => {
    const days = [];
    const daysInMonth = new Date(year, month, 0).getDate();
    const firstDayOfWeek = new Date(year, month - 1, 1).getDay();
    
    // Add empty slots for days before first day
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days
    for (let i = 1; i <= daysInMonth; i++) {
      const dateStr = `${selectedMonth}-${String(i).padStart(2, '0')}`;
      days.push({
        day: i,
        date: dateStr,
        isToday: dateStr === currentDateStr,
        isSelected: dateStr === selectedDate,
        isFuture: dateStr > currentDateStr,
      });
    }
    
    return days;
  };

  return (
    <div className={`flex flex-col gap-2 sm:gap-3 ${className}`}>
      {/* Granularity Toggle */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center bg-[hsl(225,15%,10%)] rounded-lg border border-[hsl(225,15%,15%)] p-1">
          <button
            onClick={() => onGranularityChange('daily')}
            disabled={!canViewDaily}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              viewGranularity === 'daily'
                ? 'bg-emerald-500/20 text-emerald-400'
                : canViewDaily
                  ? 'text-zinc-400 hover:text-white hover:bg-[hsl(225,15%,15%)]'
                  : 'text-zinc-600 cursor-not-allowed'
            }`}
            title={!canViewDaily ? 'Daily data only available for last 3 months' : 'View daily statistics'}
          >
            <CalendarDays className="h-4 w-4" />
            Daily
          </button>
          <button
            onClick={() => onGranularityChange('monthly')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              viewGranularity === 'monthly'
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'text-zinc-400 hover:text-white hover:bg-[hsl(225,15%,15%)]'
            }`}
            title="View monthly statistics"
          >
            <LayoutGrid className="h-4 w-4" />
            Monthly
          </button>
        </div>
        
        {!canViewDaily && (
          <div className="flex items-center gap-1.5 text-xs text-amber-400/80">
            <History className="h-3.5 w-3.5" />
            <span>Historical data (monthly only)</span>
          </div>
        )}
      </div>

      {/* Date/Month Navigation */}
      <div className="flex items-center gap-2">
        {viewGranularity === 'daily' ? (
          // Daily navigation
          <>
            <div className="flex items-center bg-[hsl(225,15%,10%)] rounded-lg border border-[hsl(225,15%,15%)] overflow-hidden">
              <button
                onClick={goToPreviousDay}
                className="p-2.5 hover:bg-[hsl(225,15%,15%)] transition-colors border-r border-[hsl(225,15%,15%)]"
                aria-label="Previous day"
              >
                <ChevronLeft className="h-4 w-4 text-zinc-400" />
              </button>
              
              <button 
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="flex items-center gap-2 px-4 py-2 min-w-[200px] justify-center hover:bg-[hsl(225,15%,12%)] transition-colors"
              >
                <CalendarDays className="h-4 w-4 text-emerald-400" />
                <span className="text-sm font-medium text-white">
                  {selectedDate ? formatDateDisplay(selectedDate) : 'Select date'}
                </span>
              </button>
              
              <button
                onClick={goToNextDay}
                disabled={!canGoNextDay}
                className={`p-2.5 transition-colors border-l border-[hsl(225,15%,15%)] ${
                  canGoNextDay 
                    ? 'hover:bg-[hsl(225,15%,15%)]' 
                    : 'opacity-40 cursor-not-allowed'
                }`}
                aria-label="Next day"
              >
                <ChevronRight className="h-4 w-4 text-zinc-400" />
              </button>
            </div>
            
            {!isToday && (
              <button
                onClick={goToToday}
                className="px-3 py-2 text-sm font-medium text-emerald-400 hover:bg-emerald-500/10 rounded-lg transition-colors"
              >
                Today
              </button>
            )}
          </>
        ) : (
          // Monthly navigation
          <>
            <div className="flex items-center bg-[hsl(225,15%,10%)] rounded-lg border border-[hsl(225,15%,15%)] overflow-hidden">
              <button
                onClick={goToPreviousMonth}
                className="p-2.5 hover:bg-[hsl(225,15%,15%)] transition-colors border-r border-[hsl(225,15%,15%)]"
                aria-label="Previous month"
              >
                <ChevronLeft className="h-4 w-4 text-zinc-400" />
              </button>
              
              <div className="flex items-center gap-2 px-4 py-2 min-w-[140px] justify-center">
                <Calendar className="h-4 w-4 text-emerald-400" />
                <span className="text-sm font-medium text-white">
                  {formatMonthDisplay(selectedMonth)}
                </span>
              </div>
              
              <button
                onClick={goToNextMonth}
                disabled={!canGoNextMonth}
                className={`p-2.5 transition-colors border-l border-[hsl(225,15%,15%)] ${
                  canGoNextMonth 
                    ? 'hover:bg-[hsl(225,15%,15%)]' 
                    : 'opacity-40 cursor-not-allowed'
                }`}
                aria-label="Next month"
              >
                <ChevronRight className="h-4 w-4 text-zinc-400" />
              </button>
            </div>
            
            {!isCurrentMonth && (
              <button
                onClick={goToCurrentMonth}
                className="px-3 py-2 text-sm font-medium text-emerald-400 hover:bg-emerald-500/10 rounded-lg transition-colors"
              >
                This Month
              </button>
            )}
          </>
        )}
      </div>

      {/* Date Picker Dropdown */}
      {showDatePicker && viewGranularity === 'daily' && (
        <div className="absolute z-50 mt-20 bg-[hsl(225,15%,10%)] border border-[hsl(225,15%,15%)] rounded-lg shadow-xl p-4">
          {/* Month navigation in picker */}
          <div className="flex items-center justify-between mb-3">
            <button 
              onClick={goToPreviousMonth}
              className="p-1.5 hover:bg-[hsl(225,15%,15%)] rounded-md transition-colors"
            >
              <ChevronLeft className="h-4 w-4 text-zinc-400" />
            </button>
            <span className="text-sm font-medium text-white">
              {formatMonthDisplay(selectedMonth)}
            </span>
            <button 
              onClick={goToNextMonth}
              disabled={!canGoNextMonth}
              className={`p-1.5 rounded-md transition-colors ${
                canGoNextMonth ? 'hover:bg-[hsl(225,15%,15%)]' : 'opacity-40'
              }`}
            >
              <ChevronRight className="h-4 w-4 text-zinc-400" />
            </button>
          </div>
          
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
              <div key={day} className="text-xs text-zinc-500 text-center py-1">
                {day}
              </div>
            ))}
          </div>
          
          {/* Days grid */}
          <div className="grid grid-cols-7 gap-1">
            {getDaysInMonth().map((item, idx) => (
              item === null ? (
                <div key={`empty-${idx}`} className="w-8 h-8" />
              ) : (
                <button
                  key={item.date}
                  onClick={() => {
                    if (!item.isFuture) {
                      onDateChange(item.date);
                      setShowDatePicker(false);
                    }
                  }}
                  disabled={item.isFuture}
                  className={`w-8 h-8 text-sm rounded-md transition-colors ${
                    item.isSelected
                      ? 'bg-emerald-500 text-white'
                      : item.isToday
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : item.isFuture
                          ? 'text-zinc-600 cursor-not-allowed'
                          : 'text-zinc-300 hover:bg-[hsl(225,15%,15%)]'
                  }`}
                >
                  {item.day}
                </button>
              )
            ))}
          </div>
        </div>
      )}
      
      {/* Click outside to close picker */}
      {showDatePicker && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowDatePicker(false)}
        />
      )}
    </div>
  );
};

export default DateSelector;

